import AwardMedal from '../AwardMedal';
import React from 'react';

// Single supplier-facing status pill – ONE place that maps a status key to its
// token colour and supplier-facing label. Consumed by the dashboard, the
// brief-detail page and the response form so the pill can never drift between
// them again (the recurring "In progress renders sage-green here, teal there"
// problem came from each surface styling its own copy).
//
// Colour meaning is shared with the internal product; only the LABELS are
// supplier-facing (Awaiting you / In progress / Submitted / Awarded / …).
//   awaiting  → amber  (the ACTION state – matches the dashboard action banner)
//   in_progress / submitted → teal (engaged, in the pipeline)
//   awarded   → emerald (reserved for awarded on both sides of the product)
//   closed    → neutral grey
//   declined / deadline_passed → soft red
export const PORTAL_STATUS = {
  awaiting:        { label: 'Awaiting your response', cls: 'text-[#7A4E10] bg-[#E89A2E]/15 border-[#E89A2E]/40' },
  in_progress:     { label: 'In progress',            cls: 'text-[#26697E] bg-[#2E8FA1]/[0.12] border-[#2E8FA1]/30' },
  submitted:       { label: 'Submitted',              cls: 'text-[#2E8FA1] bg-[#2E8FA1]/[0.12] border-[#2E8FA1]/30' },
  awarded:         { label: 'Awarded',                cls: 'text-[#009159] bg-[#009159]/[0.12] border-[#009159]/30' },
  closed:          { label: 'Closed',                 cls: 'text-[#51534A] bg-[#51534A]/[0.12] border-[#DDE1D0]' },
  declined:        { label: 'Declined',               cls: 'text-[#9c2b12] bg-[#C6503A]/[0.10] border-[#C6503A]/25' },
  deadline_passed: { label: 'Deadline passed',        cls: 'text-[#9c2b12] bg-[#C6503A]/[0.10] border-[#C6503A]/25' },
};

// Token + label for a status key (or null if unknown). Callers that render
// their own pill markup (existing bespoke chips) can consume this for the
// colours/label without adopting the component wholesale.
export function portalStatusMeta(status) {
  return PORTAL_STATUS[status] || null;
}

// Bare tones for pills that aren't brief-statuses – e.g. the account header's
// "Active supplier" + compliance state. Same token colours as PORTAL_STATUS so
// every pill in the portal reads as one system; pass `tone` + `label` instead
// of a status key. (Account brief §4: two independent states → two pills.)
export const PILL_TONE = {
  green:   'text-[#009159] bg-[#009159]/[0.12] border-[#009159]/30',
  teal:    'text-[#26697E] bg-[#2E8FA1]/[0.12] border-[#2E8FA1]/30',
  amber:   'text-[#7A4E10] bg-[#E89A2E]/15 border-[#E89A2E]/40',
  red:     'text-[#9c2b12] bg-[#C6503A]/[0.10] border-[#C6503A]/25',
  neutral: 'text-[#51534A] bg-[#51534A]/[0.10] border-[#DDE1D0]',
};

// Renders a pill from EITHER a brief-status key (`status`) OR a bare `tone`.
// `label` overrides the status default and is required with `tone`.
export default function PortalStatusPill({ status, tone, label, dot = false, className = '' }) {
  const cls = tone ? PILL_TONE[tone] : PORTAL_STATUS[status]?.cls;
  const text = label || PORTAL_STATUS[status]?.label;
  if (!cls || !text) return null;
  // The awarded pill carries the medal at 24px – the brief's list-row size, and
  // the one place the mark appears at small scale. It rides INSIDE the existing
  // pill rather than beside it so the net signal count doesn't go up: the pill
  // was already the status, the medal just makes the winning one findable in a
  // list of them. Decorative here – the pill's own text already says "Awarded",
  // so announcing it twice would only be noise to a screen reader.
  const showMedal = !tone && status === 'awarded';
  return (
    <span className={`inline-flex items-center gap-2 font-['Metropolis'] text-[11px] font-semibold rounded-full border px-3 py-1 whitespace-nowrap ${cls} ${className}`}>
      {showMedal
        ? <AwardMedal size={24} decorative className="-ml-1" />
        : dot && <span className="w-1.5 h-1.5 rounded-full bg-current flex-shrink-0" aria-hidden="true" />}
      {text}
    </span>
  );
}
